import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { saveAs } from 'file-saver';
import { useResizeObserver } from 'usehooks-ts';

import { Input, Modal, Table, Select, Tag, Switch, Form, Tooltip, Flex, notification, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    ReloadOutlined,
    QuestionCircleOutlined,
    StopOutlined,
    UnorderedListOutlined,
    FileExcelOutlined,
    TeamOutlined,
} from '@ant-design/icons';

import Tags from '@controls/tags/tags';
import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { securityRestriction } from '@src/extensions/notification';
import { exception } from '@extensions/notification';
import { Permission, hasPermission } from '@src/enums/permission';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { delayAction, dataURItoBlob } from '@extensions/utils';

import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';
import { IConsigneeFilter } from '@entities/consignee-filter';
import { IConsignee } from '@entities/consignee';
import { ICity } from '@entities/city';
import { IUser } from '@entities/user';
import { IUserFilter } from '@entities/user-filter';

import { UserType } from '@enums/user-type';

interface IConsigneeLockParams {
    lockReason?: string;
}

const filterContext: string = 'Consignees';

const Consignees = () => {
    const initFilter: IConsigneeFilter = { isArchived: false, isLockedOnly: false };

    const { TextArea } = Input;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const [lockForm] = Form.useForm();

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const fileInputRef = useRef(null);

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IConsigneeFilter>((s) => s.filters[filterContext]);

    const [entities, setEntities] = useState<Array<IConsignee>>([]);
    const [users, setUsers] = useState<Array<IUser>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [currentEntity, setCurrentEntity] = useState<IConsignee>();
    const [loading, setLoading] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    const [lockOpen, setLockOpen] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;
        const fetchData = async () => {
            const userFilter: IUserFilter = { isActive: true, isArchived: false, types: [UserType.MainClient] };

            const promises = [
                await serverFetch('users', { method: 'GET', queryParams: userFilter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUsers(result[0][0] || []);
                setCities(result[0][1] || []);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);
        onRefresh();
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onRefresh = () => {
        setEntities([]);

        setLoading(true);

        serverFetch('consignees', { method: 'GET', queryParams: filter })
            .then((data) => {
                setEntities(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    };

    const onDelete = () => {
        serverFetch(`consignees`, { method: 'DELETE', bodyData: selectedIds })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetLock = (entity: IConsigneeLockParams) => {
        serverFetch(`consignees/setlock`, {
            method: 'POST',
            bodyData: {
                ids: selectedIds,
                lockReason: entity.lockReason,
            },
        })
            .then(() => {
                lockForm.resetFields();
                setLockOpen(false);
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка блокировки грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onExport = () => {
        serverFetch('consignees/export', { method: 'GET', queryParams: filter })
            .then((data) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex) => {
                exception(api, 'Ошибка выгрузки грузополучателей', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            onRefresh();
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageConsignees)) {
                                securityRestriction(api, [Permission.ManageConsignees]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: `${
                            hasPermission(userSession.permissions, Permission.ManageConsignees)
                                ? 'Изменить'
                                : 'Информация о грузополучателе'
                        }`,
                        key: 'edit',
                        disabled: !currentEntity,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/${currentEntity?.id}`);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: selectedIds.length <= 0,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteConsignee)) {
                                securityRestriction(api, [Permission.DeleteConsignee]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить выбранных грузополучателей?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    {
                        label: 'Разблокировать / Заблокировать',
                        disabled: selectedIds.length <= 0,
                        key: 'lock',
                        icon: <StopOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.LockConsignee)) {
                                securityRestriction(api, [Permission.LockConsignee]);
                                return;
                            }

                            setLockOpen(true);
                        },
                    },
                    {
                        label: 'Заказы пользователя',
                        key: 'orders',
                        disabled: !currentEntity,
                        icon: <UnorderedListOutlined />,
                        onClick: () => {
                            navigate(`/orders/${currentEntity?.userId}`);
                        },
                    },
                    {
                        label: 'Выгрузить в Excel',
                        key: 'exportToExcel',
                        icon: <FileExcelOutlined />,
                        onClick: () => onExport(),
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Select
                            key='users'
                            placeholder='Клиент'
                            maxTagCount='responsive'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, userId: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={users.map((u) => {
                                return { value: u.id, label: u.login };
                            })}
                        />,
                        <Input
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                             style={{ width: 230 }}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            key='searchText'
                            placeholder='ИНН / Контрагент'
                            value={filter?.searchText}
                            style={{ width: 230 }}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            key='phone'
                            placeholder='Телефон'
                            value={filter?.phone}
                            style={{ width: 230 }}
                            onChange={(data) => {
                                d(setFilter({ ...filter, phone: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            maxTagCount='responsive'
                            value={filter?.cityId}
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityId: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Form.Item key='isLockedOnly' label='Заблокированные' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isLockedOnly}
                                onChange={(value) => {
                                    d(setFilter({ ...filter, isLockedOnly: value }, filterContext));
                                }}
                            />
                        </Form.Item>,

                        hasPermission(userSession.permissions, Permission.FullAccess) && (
                            <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                                <Switch
                                    checked={filter?.isArchived}
                                    onChange={(value) => {
                                        d(setFilter({ ...filter, isArchived: value }, filterContext));
                                    }}
                                />
                            </Form.Item>
                        ),
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = entities.find((o) => o.id === selectedRowKeys[0]);
            setCurrentEntity(entity);
        } else {
            setCurrentEntity(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<IConsignee> = [
            {
                title: 'Клиент',
                width: 200,
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record: IConsignee) => {
                    return (
                        <Flex align='center' justify='left' gap='small'>
                            <Tooltip
                                placement='topLeft'
                                title={record.userIsActive ? 'Личный кабинет активирован' : 'Личный кабинет не активирован'}
                                color={record.userIsActive ? 'darkgreen' : 'red'}
                            >
                                <div className={record.userIsActive ? 'active-dot' : 'inactive-dot'} />
                            </Tooltip>

                            <a
                                href={`${window.location.protocol}//${window.location.host}/settings/users/${record?.userId}`}
                                target='_blank'
                            >
                                {record.userLogin}
                            </a>
                        </Flex>
                    );
                },
            },
            {
                title: 'Маркировка',
                dataIndex: 'allMarkings',
                width: 300,
            },
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 150,
            },

            {
                title: 'Тег',
                width: 200,
                align: 'center',
                render: (_, record) => {
                    return <Tags label={record.tag} />;
                },
            },
            {
                title: 'Телефон',
                dataIndex: 'phone',
                width: 170,
            },
            {
                title: 'ИНН',
                dataIndex: 'inn',
                width: 110,
            },
            {
                title: 'Контрагент',
                dataIndex: 'legalName',
                width: 200,
            },
            {
                title: 'Юр. адрес',
                dataIndex: 'legalAddress',
                width: 300,
            },
            {
                title: 'Адрес разгрузки',
                dataIndex: 'siteAddress',
                width: 300,
            },
            {
                title: 'Адрес',
                dataIndex: 'address',
                width: 350,
            },
            {
                title: 'ФИО',
                dataIndex: 'fullName',
                width: 250,
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    columnWidth: 32,
                }}
                columns={columns}
                dataSource={entities}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
                footer={() =>
                    entities && (
                        <Flex align='center' justify='start' style={{ fontSize: 16, fontWeight: 600 }}>
                            <TeamOutlined style={{ fontSize: 20, marginRight: 5 }} />
                            <span>{entities.length}</span>
                        </Flex>
                    )
                }
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 230px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <input
                type='file'
                accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                onChange={(x: any) => {
                    const file = x.target.files[0];
                    if (file) {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => {
                            if (reader && reader.result) {
                                const fileBody = (reader.result as string).replace(
                                    'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,',
                                    '',
                                );

                                serverFetch(`consignees/import`, { method: 'POST', bodyData: fileBody })
                                    .then(() => {})
                                    .catch((ex) => {
                                        exception(api, 'Ошибкаипорта файлаа', ex, () => d(userLoaded(undefined)));
                                    });
                            }

                            reader.onerror = (error) => console.log(error);
                        };
                    }
                }}
                onClick={(event: any) => {
                    event.target.value = null;
                }}
                multiple={false}
                ref={fileInputRef}
                style={{ display: 'none' }}
            />

            <Modal
                title={
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'start' }}>
                        <QuestionCircleOutlined style={{ color: '#faad14', marginInlineEnd: 12, fontSize: 22 }} />
                        Заблокировать грузополучателей?
                    </div>
                }
                open={lockOpen}
                okText='Сохранить'
                onOk={() => lockForm.submit()}
                onCancel={() => {
                    lockForm.resetFields();
                    setLockOpen(false);
                }}
                closable={false}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    onFinish={onSetLock}
                    form={lockForm}
                    style={{ marginTop: 10 }}
                >
                    <Form.Item
                        required={true}
                        label='Причина'
                        name='lockReason'
                        rules={[{ required: true, message: 'Укажите причину блокировки' }]}
                    >
                        <TextArea autoFocus rows={4} />
                    </Form.Item>
                </Form>
            </Modal>

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Consignees;
